"use client";
import {
  LinkedText,
  pillTabClass,
  PillTabScroller,
  WhatsAppPhone,
} from "@/components/marketing";
import type { WhatsAppChatHeader } from "@/components/marketing";
import { useRovingTabs } from "@/hooks/useRovingTabs.hook";
import { fillTemplate } from "../../guidePage.util";

interface SegmentGroup {
  key: string;
  label: string;
  who: string;
  need: string;
  idea: string;
  avoid: string;
  message: string;
}

export interface SegmentExplorerCopy {
  tablistLabel: string;
  groups: SegmentGroup[];
  labels: { who: string; need: string; idea: string; avoid: string };
  // "How to" steps; `{group}` is the selected group's label.
  howSteps: string[];
  chat: WhatsAppChatHeader;
  time: string;
}

interface SegmentExplorerProps {
  copy: SegmentExplorerCopy;
}

const keyClass =
  "block text-[11.5px] font-extrabold tracking-[0.07em] text-ink-muted uppercase";

// The preview's `segment_explorer_guide`: a tab per customer group showing
// who they are, what they need, a message idea, what to avoid, the steps in
// Create Campaign and a preview of the WhatsApp message.
const SegmentExplorer = ({ copy }: SegmentExplorerProps) => {
  const { activeIndex, select, onTabKeyDown } = useRovingTabs(
    copy.groups.length,
  );
  const group = copy.groups[activeIndex];
  const fields = [
    { label: copy.labels.who, text: group.who },
    { label: copy.labels.need, text: group.need },
    { label: copy.labels.idea, text: group.idea },
    { label: copy.labels.avoid, text: group.avoid },
  ];
  return (
    <div className="grid grid-cols-1 gap-6">
      <PillTabScroller ariaLabel={copy.tablistLabel}>
        {copy.groups.map((item, index) => {
          const isActive = index === activeIndex;
          return (
            <button
              key={item.key}
              id={`segment-tab-${item.key}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls="segment-panel"
              tabIndex={isActive ? 0 : -1}
              onClick={() => select(index)}
              onKeyDown={(event) => onTabKeyDown(event, index)}
              className={pillTabClass(isActive)}
            >
              {item.label}
            </button>
          );
        })}
      </PillTabScroller>
      <div
        id="segment-panel"
        role="tabpanel"
        aria-labelledby={`segment-tab-${group.key}`}
        className="grid grid-cols-1 items-center gap-[22px] rounded-[28px] bg-white p-5 text-ink shadow-card sm:p-[26px] md:grid-cols-[minmax(0,1fr)_auto]"
      >
        <div className="grid gap-2.5">
          {fields.map((field) => (
            <p key={field.label} className="text-base leading-normal">
              <span className={keyClass}>{field.label}</span>
              {field.text}
            </p>
          ))}
          <ol className="mt-1.5 list-decimal space-y-1 pl-5 text-[15px] leading-normal">
            {copy.howSteps.map((step) => (
              <li key={step}>
                <LinkedText text={fillTemplate(step, { group: group.label })} />
              </li>
            ))}
          </ol>
        </div>
        <div className="w-full md:w-[272px]">
          <WhatsAppPhone
            chat={copy.chat}
            messages={[{ text: group.message, time: copy.time }]}
          />
        </div>
      </div>
    </div>
  );
};

export default SegmentExplorer;
